import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { Tooltip, Typography } from 'antd'
import clsx from 'clsx'
import dayjs from 'dayjs'
import useOverflow from 'hooks/useOverflow'
import PreviewFile from 'pages/building/panoramas/components/PreviewFile'
import { useRecoilState } from 'recoil'
import { currentAnnotation } from 'store/krpanoStore'
import { AnnotationType } from 'types/building'
import Krpano from 'utils/krpano'

import Modal from 'components/atoms/Modal'
import Spinner from 'components/atoms/Spinner'
import CloseIcon from 'components/icons/CloseIcon'

import ModalAlertSetting from '../../../panoramas/components/ModalAlertSetting/ModalAlertSetting'
import ViewerFilePreview from '../ViewerFilePreview'
import ViewerPdf from '../ViewerPdf'

import AnnotationHeader from './AnnotationHeader'
import AnnotationMenu from './AnnotationMenu'

import 'quill/dist/quill.snow.css'

const YOUTUBE_REGEX =
  /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/

const convertToEmbedUrl = (url: string): string => {
  if (!url) return ''
  const match = url.match(YOUTUBE_REGEX)
  return match ? `https://www.youtube.com/embed/${match[1]}` : url
}

const isYouTubeShort = (url?: string) => !!url && url.includes('/shorts/')

const addHttps = (link: string): string =>
  link.startsWith('http') ? link : `https://${link}`

const truncateText = (text: string | undefined, maxLength = 35) => {
  if (!text) return ''
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text
}

export default function AnnotationModal() {
  const qlContainerRef = useRef<HTMLDivElement>(null)
  const [selectedMenu, setSelectedMenu] = useState(0)
  const [previewFileIndex, setPreviewFileIndex] = useState<number>()
  const [isLoadingContent, setIsLoadingContent] = useState(false)
  const [isImageLoading, setIsImageLoading] = useState(false)
  const [memoContent, setMemoContent] = useState<string>('')
  const [isOpenAlertSetting, setIsOpenAlertSetting] = useState(false)
  const { hasOverflow, elementRef } = useOverflow()

  const [sortConfig, setSortConfig] = useState<{
    key: 'name' | 'updatedAt' | 'size'
    order: 'asc' | 'desc'
  }>({ key: 'updatedAt', order: 'desc' })

  const [currentSelected, setCurrentSelected] =
    useRecoilState(currentAnnotation)

  const isInfoType = currentSelected?.type === AnnotationType.INFO
  const isMovieType = currentSelected?.type === AnnotationType.MOVIE
  const isMemoType = currentSelected?.type === AnnotationType.MEMO
  const isImageType = currentSelected?.type === AnnotationType.IMAGE
  const isTour = isMovieType || isImageType
  const isMobileScreen = useMediaQuery({ query: '(max-width: 768px)' })

  const annotationType = currentSelected?.type || 0

  const sortedFiles = useMemo(() => {
    const files = [...(currentSelected?.files || [])]
    return files.sort((a, b) => {
      const { key, order } = sortConfig
      let comparison = 0
      if (key === 'name') {
        comparison = (a.name || '').localeCompare(b.name || '')
      } else if (key === 'updatedAt') {
        const dateA = dayjs(a.updatedAt || a.createdAt)
        const dateB = dayjs(b.updatedAt || b.createdAt)
        if (dateA.isSame(dateB)) {
          comparison = 0
        } else {
          comparison = dateA.isAfter(dateB) ? 1 : -1
        }
      } else if (key === 'size') {
        const sizeA = Number(a.extraInfo?.size || a.size || 0)
        const sizeB = Number(b.extraInfo?.size || b.size || 0)
        comparison = sizeA - sizeB
      }
      return order === 'asc' ? comparison : -comparison
    })
  }, [currentSelected?.files, sortConfig])

  const formatBytes = (
    bytes: number | string | undefined | null,
    decimals = 1
  ) => {
    if (!bytes) return '--'
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(Number(bytes)) / Math.log(k))
    if (i < 0) return '0 Bytes'
    return `${parseFloat((Number(bytes) / k ** i).toFixed(dm))}${sizes[i]}`
  }

  const displayList = useMemo(() => {
    const categories = [1, 2, 3, 5]
    return categories
      .map((category) => ({
        category,
        files:
          category === 5
            ? []
            : sortedFiles.filter(
                (item) => item.category === annotationType + category
              )
      }))
      .filter((item) => item.files.length || item.category === 5)
  }, [annotationType, sortedFiles])

  const itemsMenu = useMemo(() => {
    const showLink =
      !isMovieType &&
      !isInfoType &&
      (currentSelected?.link ||
        (currentSelected?.links && currentSelected.links.length > 0))
    const labels = [
      ['取扱説明書'],
      ['製作図', '機器完成図'],
      ['申送り'],
      ['リンク']
    ]

    return labels.map((labelArray, index) => {
      let length = 0
      if (index === 3) {
        length = showLink ? 1 : 0
      } else {
        length =
          displayList.find((item) => item.category === index + 1)?.files
            .length || 0
      }

      return {
        value: index + 1,
        labels: labelArray,
        length
      }
    })
  }, [
    currentSelected?.link,
    currentSelected?.links,
    isMovieType,
    isInfoType,
    displayList
  ])

  const displayMenu = useMemo(
    () => itemsMenu.filter((item) => item.length),
    [itemsMenu]
  )

  const handleSelectMenu = (value: number) => () => {
    if (selectedMenu !== value) setSelectedMenu(value)
  }

  const handleSort = useCallback((key: 'name' | 'updatedAt' | 'size') => {
    setSortConfig((prev) => {
      const isSameKey = prev.key === key
      let nextOrder: 'asc' | 'desc'
      if (isSameKey) {
        nextOrder = prev.order === 'asc' ? 'desc' : 'asc'
      } else {
        nextOrder = 'asc'
      }
      return { key, order: nextOrder }
    })
  }, [])

  const handleCloseModal = useCallback(() => {
    setCurrentSelected(undefined)
    setPreviewFileIndex(undefined)
    setMemoContent('')
  }, [setCurrentSelected])

  const handleFetchContent = useCallback(async () => {
    if (!qlContainerRef.current || !currentSelected?.memo) return

    const shouldFetch = isMemoType || isImageType || isMovieType || isInfoType
    if (!shouldFetch) return

    setIsLoadingContent(true)
    try {
      const textContent = await Krpano.FetchMemoContent(currentSelected.memo)
      qlContainerRef.current && (qlContainerRef.current.innerHTML = textContent)
    } finally {
      setIsLoadingContent(false)
    }
  }, [currentSelected?.memo, isMemoType, isImageType, isMovieType, isInfoType])

  const shouldShowMainContent = isImageType || isMovieType
  const shouldShowFileList =
    (displayMenu.length > 0 && !isMovieType) || isInfoType

  useEffect(() => {
    if (isInfoType && currentSelected?.memo_content) {
      setPreviewFileIndex(0)
    }
  }, [isInfoType, currentSelected?.memo_content])

  useEffect(() => {
    handleFetchContent()
  }, [handleFetchContent])

  useEffect(() => {
    if (currentSelected?.memo_content && isImageType) {
      setIsImageLoading(true)

      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.src = currentSelected.memo_content
      img.onload = () => setIsImageLoading(false)
      img.onerror = () => setIsImageLoading(false)
    }
  }, [currentSelected?.memo_content, isImageType])

  const pdfFileObject = useMemo(() => {
    if (!isInfoType || !currentSelected?.memo_content) return null

    return {
      id: Date.now(),
      s3Path: currentSelected.memo_content,
      name: currentSelected.memo_file_name || 'annotation-document.pdf',
      extension: 'pdf',
      mimeType: 'application/pdf',
      createdAt: new Date().toISOString(),
      category: 140,
      thumbnail: '',
      pointId: null,
      takenAt: null,
      tilesUrl: null,
      multires: null,
      note: ''
    }
  }, [
    isInfoType,
    currentSelected?.memo_content,
    currentSelected?.memo_file_name
  ])

  const showAlertSetting = false

  // --- No selection or still loading type: render nothing ---

  if (!currentSelected) return null
  if (!currentSelected.type && currentSelected.isMutating) return null

  // --- Empty state ---
  if (
    (isImageType || isMovieType || isInfoType) &&
    !currentSelected?.memo_content &&
    !currentSelected?.memo
  ) {
    if (isTour) {
      return (
        <>
          <div
            className="fixed top-5 right-5 cursor-pointer z-50"
            onClick={handleCloseModal}
          >
            <CloseIcon />
          </div>
          <Modal
            rootClassName="annotation-detail-modal"
            maskClosable
            onCancel={handleCloseModal}
          >
            <AnnotationHeader
              type={currentSelected?.type}
              alertId={currentSelected?.alert?.id}
              showAlertSetting={showAlertSetting}
              onOpenAlertSetting={() => setIsOpenAlertSetting(true)}
            />
            <div className="w-full flex items-center justify-center">
              <span className="text-gray-500 text-center text-sm">
                データがありません
              </span>
            </div>
          </Modal>
        </>
      )
    }

    return (
      <Modal
        width="740px"
        open={previewFileIndex === undefined}
        rootClassName="annotation-detail-modal"
        maskClosable
        onCancel={handleCloseModal}
      >
        <div className="absolute top-5 right-5 flex items-center gap-4 z-10">
          <div className="cursor-pointer" onClick={handleCloseModal}>
            <CloseIcon />
          </div>
        </div>
        <AnnotationHeader
          type={currentSelected?.type}
          alertId={currentSelected?.alert?.id}
          showAlertSetting={showAlertSetting}
          onOpenAlertSetting={() => setIsOpenAlertSetting(true)}
        />
        <div className="w-full flex h-[400px] items-center justify-center">
          <span className="text-gray-500 text-center">データがありません</span>
        </div>
      </Modal>
    )
  }

  if (isInfoType && currentSelected?.memo_content && pdfFileObject) {
    return <ViewerPdf file={pdfFileObject} handleCloseFile={handleCloseModal} />
  }
  // --- Shared file list / links section ---

  const renderFileListSection = () => (
    <>
      {!isInfoType && (
        <AnnotationMenu
          items={displayMenu}
          selectedMenu={selectedMenu}
          handleSelect={handleSelectMenu}
        />
      )}
      <div
        className={clsx('mt-[10px] mx-5 overflow-auto', {
          'h-[320px]': !isTour
        })}
      >
        {(() => {
          if (isInfoType) {
            if (currentSelected?.memo_content) {
              return (
                <div className="flex justify-center items-center h-full">
                  <div className="text-center">
                    <Typography className="text-[#021120] text-sm">
                      {currentSelected.memo_file_name ||
                        'annotation-document.pdf'}
                    </Typography>
                  </div>
                </div>
              )
            }
            return (
              <div className="w-full flex flex-col items-center justify-center py-12">
                <span className="text-gray-500 text-center">
                  データがありません
                </span>
              </div>
            )
          }

          if (displayList[selectedMenu]?.category !== 5) {
            const files = displayList[selectedMenu]?.files || []
            return (
              <div className="flex flex-col h-full overflow-hidden">
                <div
                  className="grid items-center px-1 py-2 border-0 border-b border-solid border-[#EEEFF2] mb-3"
                  style={{
                    gridTemplateColumns: `120px 1fr 120px 100px`,
                    gap: '12px'
                  }}
                >
                  <div />
                  <div
                    className="flex items-center gap-1 cursor-pointer select-none"
                    onClick={() => handleSort('name')}
                  >
                    <Typography
                      className={clsx(
                        'font-bold text-[13px] whitespace-nowrap',
                        {
                          'theme-text-primary': sortConfig.key === 'name',
                          'text-[#A0A0A0]': sortConfig.key !== 'name'
                        }
                      )}
                    >
                      名前
                      {sortConfig.key === 'name' &&
                        (sortConfig.order === 'asc' ? ' ↑' : ' ↓')}
                    </Typography>
                  </div>
                  <div
                    className="flex items-center gap-1 cursor-pointer select-none"
                    onClick={() => handleSort('updatedAt')}
                  >
                    <Typography
                      className={clsx(
                        'font-bold text-[13px] whitespace-nowrap',
                        {
                          'theme-text-primary': sortConfig.key === 'updatedAt',
                          'text-[#A0A0A0]': sortConfig.key !== 'updatedAt'
                        }
                      )}
                    >
                      更新日
                      {sortConfig.key === 'updatedAt' &&
                        (sortConfig.order === 'asc' ? ' ↑' : ' ↓')}
                    </Typography>
                  </div>
                  <div>
                    <Typography className="font-bold text-[13px] whitespace-nowrap text-[#A0A0A0]">
                      サイズ
                    </Typography>
                  </div>
                </div>

                {files.length > 0 ? (
                  <div className="grid gap-3 overflow-y-auto">
                    {files.map((item, index) => (
                      <div
                        className="grid items-center cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors"
                        style={{
                          gridTemplateColumns: `120px 1fr 120px 100px`,
                          gap: '12px'
                        }}
                        key={item.id}
                        onClick={() => setPreviewFileIndex(index)}
                      >
                        <PreviewFile file={item} size="large" />
                        <Typography className="text-[#021120] text-sm truncate">
                          {item.name}
                        </Typography>
                        <Typography className="text-[#021120] text-[13px] whitespace-nowrap">
                          {dayjs(item.updatedAt || item.createdAt).format(
                            'YYYY/MM/DD'
                          )}
                        </Typography>
                        <Typography className="text-[#021120] text-[13px] whitespace-nowrap">
                          {item.extension?.toLowerCase() === 'pdf' &&
                          item.extraInfo?.page_count
                            ? `${item.extraInfo.page_count} ページ`
                            : formatBytes(item.extraInfo?.size || item.size)}
                        </Typography>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="w-full flex flex-col items-center justify-center py-12">
                    <span className="text-gray-500 text-center">
                      データがありません
                    </span>
                  </div>
                )}
              </div>
            )
          }

          const currentLinks = currentSelected?.links || []

          const validLinks = currentLinks.filter((linkItem) => {
            const name = linkItem?.name?.trim()
            const link = linkItem?.link?.trim()
            return name || link
          })

          const hasLinks = validLinks.length > 0 || !!currentSelected?.link

          if (hasLinks) {
            return (
              <div className="flex flex-col gap-8 items-center">
                {currentLinks.length > 0 ? (
                  <div className="flex flex-col gap-12 w-full items-center">
                    {currentLinks.map((linkItem, idx) => {
                      const text = linkItem.name || linkItem.link
                      return (
                        <div
                          key={`link-${linkItem.link || idx}`}
                          className="text-center max-w-full px-10"
                        >
                          <Tooltip title={text}>
                            {linkItem.link ? (
                              <a
                                href={addHttps(linkItem.link || '')}
                                target="_blank"
                                rel="noreferrer"
                                className="theme-text-primary text-sm underline block"
                              >
                                {truncateText(text)}
                              </a>
                            ) : (
                              <span className=" text-sm block">
                                {truncateText(text)}
                              </span>
                            )}
                          </Tooltip>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-center max-w-full px-10">
                    <Tooltip
                      title={currentSelected?.name || currentSelected?.link}
                    >
                      <a
                        href={addHttps(currentSelected?.link || '')}
                        target="_blank"
                        rel="noreferrer"
                        className="theme-text-primary text-sm underline block"
                      >
                        {truncateText(
                          currentSelected?.name || currentSelected?.link || ''
                        )}
                      </a>
                    </Tooltip>
                  </div>
                )}
              </div>
            )
          }

          return (
            <div className="w-full flex flex-col items-center justify-center py-12">
              <span className="text-gray-500 text-center">
                データがありません
              </span>
            </div>
          )
        })()}
      </div>
    </>
  )

  // --- Tour variant ---

  if (isTour) {
    return (
      <>
        <Modal
          width={isMobileScreen ? '100vw' : '90vw'}
          bodyStyle={{
            height: isMobileScreen ? '100vh' : '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}
          rootClassName="custom-annotation-detail-modal"
          maskClosable
          onCancel={handleCloseModal}
        >
          <div
            className={`absolute top-3 right-3 cursor-pointer z-50 text-white leading-none select-none ${
              isMobileScreen ? 'text-md' : 'text-2xl'
            }`}
            onClick={handleCloseModal}
          >
            ×
          </div>

          <Spinner
            spinning={
              (currentSelected?.isMutating && previewFileIndex === undefined) ||
              isImageLoading ||
              isLoadingContent
            }
            className="w-full h-full overflow-hidden"
          >
            <div className="flex flex-col h-full overflow-hidden">
              {shouldShowMainContent && (
                <div className={isMobileScreen ? 'mx-2' : 'mx-5'}>
                  {(() => {
                    const hasMediaAndMemo =
                      currentSelected?.memo && currentSelected?.memo_content
                    const hasMemoOnly =
                      currentSelected?.memo && !currentSelected?.memo_content

                    if (hasMediaAndMemo) {
                      return (
                        <div className="annotation-media-memo-layout">
                          {/* Media column */}
                          <div
                            className={`annotation-media-col ${
                              isImageType
                                ? 'annotation-media-col-image'
                                : 'annotation-media-col-movie'
                            }`}
                          >
                            {isImageType && currentSelected?.memo_content && (
                              <div className="flex justify-center relative">
                                <link
                                  rel="preload"
                                  as="image"
                                  href={currentSelected.memo_content}
                                  type="image/jpeg"
                                />
                                {/* Desktop wrapper */}
                                <div className="hidden md:flex w-full overflow-auto items-center justify-center max-h-[87vh]">
                                  <img
                                    src={currentSelected.memo_content}
                                    alt="Annotation"
                                    style={{
                                      width: '100%',
                                      height: 'auto',
                                      maxWidth: '100%',
                                      maxHeight: '100%',
                                      objectFit: 'contain',
                                      objectPosition: 'center'
                                    }}
                                    loading="eager"
                                    decoding="sync"
                                    onLoad={() => setIsImageLoading(false)} // bỏ img.style.display
                                    onError={(e) => {
                                      const img = e.target as HTMLImageElement
                                      img.style.visibility = 'hidden' // dùng visibility thay vì display
                                      setIsImageLoading(false)
                                    }}
                                    className="shadow-md"
                                  />
                                </div>

                                {/* Mobile image */}
                                <img
                                  src={currentSelected.memo_content}
                                  alt="Annotation"
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                    objectPosition: 'center'
                                  }}
                                  loading="eager"
                                  decoding="sync"
                                  onLoad={() => setIsImageLoading(false)} // bỏ img.style.display
                                  onError={(e) => {
                                    const img = e.target as HTMLImageElement
                                    img.style.visibility = 'hidden'
                                    setIsImageLoading(false)
                                  }}
                                  className="shadow-md md:hidden"
                                />
                              </div>
                            )}

                            {isMovieType && currentSelected?.memo_content && (
                              <div className="flex justify-center">
                                <iframe
                                  src={convertToEmbedUrl(
                                    currentSelected.memo_content
                                  )}
                                  title="YouTube Video"
                                  style={
                                    isYouTubeShort(currentSelected.memo_content)
                                      ? {
                                          height: isMobileScreen
                                            ? '45vh'
                                            : '87vh',
                                          aspectRatio: '9 / 16'
                                        }
                                      : {
                                          width: isMobileScreen
                                            ? '100%'
                                            : '100%',
                                          height: isMobileScreen
                                            ? '35vh'
                                            : '87vh',
                                          aspectRatio: isMobileScreen
                                            ? '16 / 9'
                                            : ''
                                        }
                                  }
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              </div>
                            )}
                          </div>

                          {/* Text column — single ref, always rendered */}
                          <div
                            className={`annotation-text-col annotation-text-col-${
                              isImageType ? 'image' : 'movie'
                            }`}
                          >
                            <div
                              ref={elementRef}
                              className={`ql-container overflow-y-auto memo-container !h-auto pt-1 text-white w-full ${
                                hasOverflow
                                  ? 'scrollbar-visible'
                                  : 'scrollbar-hidden'
                              }`}
                            >
                              <div
                                id="quill-reader"
                                ref={qlContainerRef}
                                className={`flex flex-col ql-editor quill-editor-custom quill-editor-custom-auto-scroll ${
                                  hasOverflow
                                    ? 'justify-center'
                                    : 'justify-start'
                                }`}
                              />
                            </div>
                          </div>
                        </div>
                      )
                    }

                    if (hasMemoOnly) {
                      return (
                        <div className="w-full h-full">
                          <div
                            ref={elementRef}
                            className={`ql-container overflow-y-auto memo-container !h-auto pt-1 text-white ${
                              hasOverflow
                                ? 'scrollbar-visible'
                                : 'scrollbar-hidden'
                            }`}
                          >
                            <div
                              id="quill-reader"
                              ref={qlContainerRef}
                              className={`flex flex-col ql-editor quill-editor-custom quill-editor-custom-auto-scroll max-h-[87vh] ${
                                hasOverflow ? 'justify-center' : 'justify-start'
                              }`}
                            />
                          </div>
                        </div>
                      )
                    }

                    return (
                      <div className="w-full">
                        {isImageType && currentSelected?.memo_content && (
                          <div className="flex justify-center relative">
                            <link
                              rel="preload"
                              as="image"
                              href={currentSelected.memo_content}
                              type="image/jpeg"
                            />
                            <div
                              className="w-full flex items-center justify-center"
                              style={{ height: '87vh' }}
                            >
                              <img
                                src={currentSelected.memo_content}
                                alt="Annotation"
                                style={{
                                  width: 'auto',
                                  height: '100%',
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  objectFit: 'contain',
                                  objectPosition: 'center'
                                }}
                                loading="eager"
                                decoding="sync"
                                onLoad={(e) => {
                                  const img = e.target as HTMLImageElement
                                  img.style.display = 'block'
                                  setIsImageLoading(false)
                                }}
                                onError={(e) => {
                                  const img = e.target as HTMLImageElement
                                  img.style.display = 'none'
                                  setIsImageLoading(false)
                                }}
                                className="shadow-md"
                              />
                            </div>
                          </div>
                        )}

                        {isMovieType && currentSelected?.memo_content && (
                          <div className="flex justify-center items-center">
                            <iframe
                              src={convertToEmbedUrl(
                                currentSelected.memo_content
                              )}
                              title="YouTube Video"
                              style={
                                isYouTubeShort(currentSelected.memo_content)
                                  ? { height: '87vh', aspectRatio: '9 / 16' }
                                  : {
                                      width: '90%',
                                      height: isMobileScreen ? '30vh' : '87vh'
                                    }
                              }
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        )}
                      </div>
                    )
                  })()}
                </div>
              )}

              {!isMemoType && shouldShowFileList && renderFileListSection()}

              {isMemoType && memoContent && (
                <div className="mt-[10px] mx-5 overflow-auto">
                  <div className="ql-container ql-snow memo-container !h-auto min-h-[11rem]">
                    <div
                      ref={qlContainerRef}
                      className="ql-editor quill-editor-custom"
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: memoContent }}
                    />
                  </div>
                </div>
              )}
            </div>
          </Spinner>
        </Modal>

        {previewFileIndex !== undefined &&
          !isInfoType &&
          displayList[selectedMenu]?.files[previewFileIndex] && (
            <ViewerFilePreview
              fileIndex={previewFileIndex}
              file={displayList[selectedMenu].files[previewFileIndex]}
              maxLength={displayList[selectedMenu].files.length}
              setPreviewFileIndex={setPreviewFileIndex}
            />
          )}
      </>
    )
  }

  // --- Default variant ---

  return (
    <>
      <Modal
        width="740px"
        open={previewFileIndex === undefined}
        rootClassName="annotation-detail-modal"
        maskClosable
        onCancel={handleCloseModal}
      >
        <div className="absolute top-5 right-5 flex items-center gap-4 z-10">
          <div className="cursor-pointer" onClick={handleCloseModal}>
            <CloseIcon />
          </div>
        </div>
        <AnnotationHeader
          type={currentSelected?.type}
          alertId={currentSelected?.alert?.id}
          showAlertSetting={showAlertSetting}
          onOpenAlertSetting={() => setIsOpenAlertSetting(true)}
        />

        <Spinner
          spinning={
            (currentSelected?.isMutating && previewFileIndex === undefined) ||
            isImageLoading ||
            isLoadingContent
          }
          className="w-full"
        >
          <div className="min-h-[320px] max-h-[500px]">
            {shouldShowMainContent && (
              <div className="mx-5 max-h-[500px]">
                <div className="flex flex-col">
                  {isImageType && currentSelected?.memo_content && (
                    <div className="flex justify-center relative w-full">
                      <link
                        rel="preload"
                        as="image"
                        href={currentSelected.memo_content}
                        type="image/jpeg"
                      />
                      <div className="w-[auto] max-h-[320px] overflow-auto flex items-center justify-center">
                        <img
                          src={currentSelected.memo_content}
                          alt="Annotation"
                          style={{
                            width: 'auto',
                            height: 'auto',
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            objectPosition: 'center'
                          }}
                          loading="eager"
                          decoding="sync"
                          onLoad={(e) => {
                            const img = e.target as HTMLImageElement
                            img.style.display = 'block'
                            setIsImageLoading(false)
                          }}
                          onError={(e) => {
                            const img = e.target as HTMLImageElement
                            img.style.display = 'none'
                            setIsImageLoading(false)
                          }}
                          className="shadow-md"
                        />
                      </div>
                    </div>
                  )}

                  {isMovieType && currentSelected?.memo_content && (
                    <div className="flex justify-center">
                      <iframe
                        src={convertToEmbedUrl(currentSelected.memo_content)}
                        title="YouTube Video"
                        style={{ width: '500px', height: '320px' }}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}

                  {!(isImageType || isMovieType) &&
                    !currentSelected?.memo_content &&
                    !currentSelected?.memo && (
                      <div className="w-full flex flex-col items-center justify-center py-12">
                        <span className="text-gray-500 text-center">
                          データがありません
                        </span>
                      </div>
                    )}

                  {currentSelected?.memo && (
                    <div
                      ref={elementRef}
                      className={`ql-container overflow-y-auto memo-container pt-1 ${
                        currentSelected.memo_content
                          ? 'min-h-[9rem] max-h-[200px]'
                          : 'max-h-[500px] flex-1 scrollbar-visible'
                      } ${
                        hasOverflow ? 'scrollbar-visible' : 'scrollbar-hidden'
                      }`}
                    >
                      <div
                        ref={qlContainerRef}
                        className={`ql-editor quill-editor-custom ${
                          currentSelected.memo_content
                            ? 'scrollbar-visible'
                            : 'scrollbar-hidden'
                        }`}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {!isMemoType && shouldShowFileList && renderFileListSection()}

            {isMemoType && memoContent && (
              <div className="mt-[30px] mx-5 max-h-[400px] overflow-auto">
                <div className="ql-container ql-snow memo-container min-h-[11rem]">
                  <div
                    ref={qlContainerRef}
                    className="ql-editor quill-editor-custom"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: memoContent }}
                  />
                </div>
              </div>
            )}
          </div>
        </Spinner>
      </Modal>

      {previewFileIndex !== undefined &&
        !isInfoType &&
        displayList[selectedMenu]?.files[previewFileIndex] && (
          <ViewerFilePreview
            fileIndex={previewFileIndex}
            file={displayList[selectedMenu].files[previewFileIndex]}
            maxLength={displayList[selectedMenu].files.length}
            setPreviewFileIndex={setPreviewFileIndex}
          />
        )}
      {isOpenAlertSetting && (
        <ModalAlertSetting
          handleCloseModal={() => setIsOpenAlertSetting(false)}
          onSaveSuccess={(res: any) => {
            if (currentSelected) {
              const newAlertId =
                res?.data?.id ||
                res?.id ||
                res?.data?.alert?.id ||
                res?.alert?.id ||
                undefined
              if (newAlertId) {
                // Update the current selected state with the new alert ID
                setCurrentSelected({
                  ...currentSelected,
                  alert: { id: newAlertId }
                })
              }
            }
            setIsOpenAlertSetting(false)
          }}
          onDeleteSuccess={() => {
            if (currentSelected) {
              setCurrentSelected({ ...currentSelected, alert: undefined })
            }
            setIsOpenAlertSetting(false)
          }}
          buildingId={String(currentSelected?.building_id || 0)}
          floorId={currentSelected?.floor_id}
          annotationId={
            currentSelected?.id ? String(currentSelected.id) : undefined
          }
          pointId={currentSelected?.point_id}
          alertId={currentSelected?.alert?.id}
        />
      )}
    </>
  )
}
